import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IconTool, IconFolder, IconSave, IconTrash, IconCpu, IconBarChart, IconCheckCircle } from './Icons';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000' 
  : window.location.origin;

const AIKernelBuilderScreen = ({ token, onBack, onKernelsUpdated }) => {
  const [industry, setIndustry] = useState('products');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Состояния для workflow
  const [step, setStep] = useState('upload'); // upload, analyzing, review, updated
  const [uploadedTexts, setUploadedTexts] = useState([]);
  const [currentKernels, setCurrentKernels] = useState({});
  const [newKeywords, setNewKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [analysisStats, setAnalysisStats] = useState(null);

  // Загрузка текущих ядер
  useEffect(() => {
    loadCurrentKernels();
  }, [industry]);

  const loadCurrentKernels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/kernels/${industry}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setCurrentKernels(response.data.kernels);
      }
    } catch (err) {
      console.error('Error loading kernels:', err);
    }
  };

  // Загрузка файла
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('industry', industry);

      const response = await axios.post(`${API_BASE_URL}/api/kernel-builder/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setUploadedTexts(response.data.texts);
        // Переходим к анализу
        setStep('analyzing');
        // Запускаем анализ
        await analyzeTexts(response.data.texts);
      }
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  // AI анализ текстов
  const analyzeTexts = async (texts) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/kernel-builder/analyze`, {
        texts: texts,
        industry: industry,
        current_kernels: currentKernels
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setNewKeywords(response.data.new_keywords);
        setAnalysisStats(response.data.stats);
        setStep('review');
      } else {
        setError('Ошибка анализа: ' + (response.data.error || 'Unknown error'));
        setStep('upload');
      }
    } catch (err) {
      setError('Ошибка анализа: ' + (err.response?.data?.error || err.message));
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };

  // Выбор/отмена ключевого слова
  const toggleKeyword = (keyword) => {
    if (selectedKeywords.includes(keyword)) {
      setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
    } else {
      setSelectedKeywords([...selectedKeywords, keyword]);
    }
  };

  // Выбрать все
  const selectAll = () => {
    setSelectedKeywords(newKeywords.map(k => k.word));
  };

  // Отменить все
  const deselectAll = () => {
    setSelectedKeywords([]);
  };

  // Добавить выбранные слова в ядра
  const addToKernels = async () => {
    if (selectedKeywords.length === 0) {
      setError('Выберите хотя бы одно слово');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/kernel-builder/add-keywords`, {
        industry: industry,
        keywords: selectedKeywords,
        current_kernels: currentKernels
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setCurrentKernels(response.data.updated_kernels);
        setStep('updated');
        setSuccess(`Добавлено ${selectedKeywords.length} новых ключевых слов`);
        
        // Уведомляем родителя об обновлении
        if (onKernelsUpdated) {
          onKernelsUpdated(response.data.updated_kernels);
        }
      }
    } catch (err) {
      setError('Ошибка сохранения: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Вернуться к началу
  const handleRestart = () => {
    setStep('upload');
    setUploadedTexts([]);
    setNewKeywords([]);
    setSelectedKeywords([]);
    setAnalysisStats(null);
    setError('');
    setSuccess('');
  };

  // Просмотр текущих ядер
  const [showCurrentKernels, setShowCurrentKernels] = useState(false);
  
  // Управление отраслями
  const [newIndustryName, setNewIndustryName] = useState('');
  
  // Переименовать отрасль (сохранить ядра под новым именем)
  const handleRenameIndustry = async () => {
    if (!newIndustryName.trim()) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/kernel-builder/rename-industry`, {
        source_industry: 'generic',
        new_name: newIndustryName.trim()
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSuccess(`Отрасль сохранена как "${response.data.new_industry}"! Теперь выбери её в списке отраслей.`);
        setNewIndustryName('');
        // Обновляем список отраслей
        await loadAvailableIndustries();
      }
    } catch (err) {
      setError('Ошибка: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Очистить отрасль
  const handleClearIndustry = async () => {
    if (!window.confirm('Точно очистить "Другое"? Все ядра будут удалены!')) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/kernel-builder/clear-industry`, {
        industry: 'generic'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSuccess(`"Другое" очищено! Удалено файлов: ${response.data.deleted_count}`);
        setCurrentKernels({});
      }
    } catch (err) {
      setError('Ошибка: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Загрузить список доступных отраслей
  const [availableIndustries, setAvailableIndustries] = useState([]);
  
  const loadAvailableIndustries = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/kernel-builder/industries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setAvailableIndustries(response.data.industries);
      }
    } catch (err) {
      console.error('Error loading industries:', err);
    }
  };
  
  // Загружаем отрасли при монтировании
  useEffect(() => {
    loadAvailableIndustries();
  }, []);

  return (
    <div className="kernel-builder-screen">
      <div className="kernel-builder-header" style={{background: '#007bff', padding: '30px', borderRadius: '15px', color: 'white', marginBottom: '30px'}}>
        <h2 style={{color: 'white', marginBottom: '10px'}}><IconTool size={22} style={{ marginRight: '8px', marginTop: '-3px' }} />Разработчик ядра ИИ</h2>
        <p style={{color: 'white', margin: 0}}>Улучшайте семантические ядра на основе новых данных</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Индикатор шагов */}
      <div className="progress-steps" style={{marginBottom: '30px'}}>
        <div className={`step ${step === 'upload' || step === 'analyzing' ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Загрузка</div>
        </div>
        <div className={`step ${step === 'analyzing' ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">ИИ Анализ</div>
        </div>
        <div className={`step ${step === 'review' ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Обзор</div>
        </div>
        <div className={`step ${step === 'updated' ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <div className="step-label">Готово</div>
        </div>
      </div>

      {/* Шаг 1: Загрузка файла */}
      {step === 'upload' && (
        <div className="upload-section">
          <div className="industry-selector">
            <label>Отрасль:</label>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)}>
              <optgroup label="Стандартные">
                <option value="products">Продукты питания</option>
                <option value="non_food">Непродовольственные товары</option>
                <option value="organoleptic">Органолептика</option>
                <option value="services">Услуги</option>
                <option value="banks">Банки и финансы</option>
              </optgroup>
              <optgroup label="Пользовательские">
                <option value="generic">Другое (Generic)</option>
                {availableIndustries.filter(ind => !['products', 'non_food', 'organoleptic', 'services', 'banks', 'generic'].includes(ind)).map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="upload-box">
            <h3><IconFolder size={18} style={{ marginRight: '8px', marginTop: '-3px' }} />Загрузите файл с ответами респондентов</h3>
            <p>Поддерживаемые форматы: Excel (.xlsx, .xls), CSV, TXT</p>
            <p>Файл должен содержать текстовые ответы на открытые вопросы</p>
            
            <input
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              onChange={handleFileUpload}
              disabled={loading}
              id="kernel-file-input"
              style={{ display: 'none' }}
            />
            <label htmlFor="kernel-file-input" className="upload-button">
              {loading ? 'Загрузка...' : 'Выбрать файл'}
            </label>
          </div>

          <div className="current-kernels-toggle">
            <button 
              onClick={() => setShowCurrentKernels(!showCurrentKernels)}
              className="btn-secondary"
            >
              {showCurrentKernels ? 'Скрыть' : 'Показать'} текущие ядра ({Object.keys(currentKernels).length} категорий)
            </button>
          </div>

          {showCurrentKernels && (
            <div className="current-kernels-view">
              <h4>Текущие категории и ключевые слова:</h4>
              {Object.entries(currentKernels).map(([category, keywords]) => (
                <div key={category} className="kernel-category">
                  <strong>{category}:</strong> {keywords.slice(0, 10).join(', ')}
                  {keywords.length > 10 && ` +${keywords.length - 10} слов`}
                </div>
              ))}
            </div>
          )}

          {/* Управление отраслями (только для generic) */}
          {industry === 'generic' && Object.keys(currentKernels).length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: 'rgba(255, 152, 0, 0.12)',
              borderRadius: '8px',
              border: '2px solid #ff9800'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#ffb74d' }}>Управление отраслью</h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '13px' }}>
                Сохранить ядра как новую отрасль или очистить "Другое" для новых данных.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Название новой отрасли (например: banking)"
                  value={newIndustryName}
                  onChange={(e) => setNewIndustryName(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--panel-2)',
                    color: 'var(--text)',
                    flex: '1',
                    minWidth: '200px'
                  }}
                />
                <button
                  onClick={handleRenameIndustry}
                  disabled={!newIndustryName.trim() || loading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: !newIndustryName.trim() || loading ? 'var(--border)' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !newIndustryName.trim() || loading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? '...' : (<><IconSave size={14} style={{ marginRight: '5px', marginTop: '-2px' }} />Сохранить как новую отрасль</>)}
                </button>
                <button
                  onClick={handleClearIndustry}
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: loading ? 'var(--border)' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? '...' : (<><IconTrash size={14} style={{ marginRight: '5px', marginTop: '-2px' }} />Очистить "Другое"</>)}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Шаг 2: Анализ */}
      {step === 'analyzing' && (
        <div className="analyzing-section">
          <div className="spinner"></div>
          <h3><IconCpu size={18} style={{ marginRight: '8px', marginTop: '-3px' }} />ИИ анализирует тексты...</h3>
          <p>Обработано {uploadedTexts.length} ответов</p>
          <p>Ищем новые ключевые слова и паттерны</p>
        </div>
      )}

      {/* Шаг 3: Обзор новых слов */}
      {step === 'review' && (
        <div className="review-section">
          <div className="analysis-stats">
            <h3><IconBarChart size={18} style={{ marginRight: '8px', marginTop: '-3px' }} />Результаты анализа</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{analysisStats?.total_texts || uploadedTexts.length}</span>
                <span className="stat-label">Проанализировано текстов</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{newKeywords.length}</span>
                <span className="stat-label">Новых ключевых слов найдено</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{selectedKeywords.length}</span>
                <span className="stat-label">Выбрано для добавления</span>
              </div>
            </div>
          </div>

          <div className="keywords-selection">
            <div className="selection-header">
              <h4>Новые ключевые слова (не найдены в текущих ядрах):</h4>
              <div className="selection-actions">
                <button onClick={selectAll} className="btn-small">Выбрать все</button>
                <button onClick={deselectAll} className="btn-small">Отменить все</button>
              </div>
            </div>

            {newKeywords.length === 0 ? (
              <p className="no-keywords">Новых ключевых слов не найдено. Все слова уже есть в ядрах.</p>
            ) : (
              <div className="keywords-grid">
                {newKeywords.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`keyword-item ${selectedKeywords.includes(item.word) ? 'selected' : ''}`}
                    onClick={() => toggleKeyword(item.word)}
                  >
                    <div className="keyword-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedKeywords.includes(item.word)}
                        onChange={() => {}}
                      />
                    </div>
                    <div className="keyword-info">
                      <span className="keyword-word">{item.word}</span>
                      <span className="keyword-freq">{item.frequency} упоминаний</span>
                      <span className="keyword-examples">Пример: "{item.example}"</span>
                    </div>
                    <div className="keyword-suggested-category">
                      Предлагаемая категория: <strong>{item.suggested_category}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="review-actions">
            <button onClick={handleRestart} className="btn-secondary">
              ← Загрузить другой файл
            </button>
            <button 
              onClick={addToKernels} 
              disabled={selectedKeywords.length === 0 || loading}
              className="btn-primary"
            >
              {loading ? 'Сохранение...' : `Добавить ${selectedKeywords.length} слов в ядра →`}
            </button>
          </div>
        </div>
      )}

      {/* Шаг 4: Готово */}
      {step === 'updated' && (
        <div className="updated-section">
          <div className="success-icon" style={{ color: '#4CAF50' }}><IconCheckCircle size={56} /></div>
          <h3>Ядра успешно обновлены!</h3>
          <p>Добавлено {selectedKeywords.length} новых ключевых слов</p>
          
          <div className="updated-actions">
            <button onClick={handleRestart} className="btn-secondary">
              Загрузить ещё файл
            </button>
            <button onClick={onBack} className="btn-primary">
              ← Вернуться к кластеризации
            </button>
          </div>
        </div>
      )}

      <style>{`
        .kernel-builder-screen {
          padding: 20px;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .kernel-builder-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .kernel-builder-header h2 {
          color: var(--text);
          margin-bottom: 10px;
        }
        
        .upload-section {
          background: var(--panel);
          border: 1px solid var(--border);
          padding: 30px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .industry-selector {
          margin-bottom: 20px;
        }
        
        .industry-selector label {
          font-weight: bold;
          margin-right: 10px;
          color: var(--text);
        }
        
        .industry-selector select {
          padding: 8px 15px;
          border: 1px solid var(--border);
          border-radius: 4px;
          background: var(--panel-2);
          color: var(--text);
        }
        
        .upload-box {
          text-align: center;
          padding: 40px;
          border: 2px dashed var(--border);
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .upload-box h3 {
          margin-bottom: 10px;
          color: var(--text);
        }
        
        .upload-box p {
          color: var(--muted);
          margin: 5px 0;
        }
        
        .upload-button {
          display: inline-block;
          background: #007bff;
          color: white;
          padding: 12px 30px;
          border-radius: 4px;
          cursor: pointer;
          margin-top: 20px;
          transition: background 0.2s;
        }
        
        .upload-button:hover {
          background: #3399ff;
        }
        
        .analyzing-section {
          text-align: center;
          padding: 60px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .analyzing-section h3 {
          color: var(--text);
        }
        
        .analyzing-section p {
          color: var(--muted);
        }
        
        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid var(--border);
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .analysis-stats {
          background: var(--panel);
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          border: 1px solid var(--border);
        }
        
        .analysis-stats h3 {
          color: var(--text);
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-top: 15px;
        }
        
        .stat-item {
          text-align: center;
        }
        
        .stat-value {
          display: block;
          font-size: 32px;
          font-weight: bold;
          color: #64b5f6;
        }
        
        .stat-label {
          font-size: 12px;
          color: var(--muted);
        }
        
        .keywords-selection {
          background: var(--panel);
          border: 1px solid var(--border);
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .selection-header h4 {
          color: var(--text);
          margin: 0;
        }
        
        .keywords-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        
        .keyword-item {
          display: grid;
          grid-template-columns: 40px 1fr 200px;
          align-items: center;
          padding: 15px;
          border: 1px solid var(--border);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .keyword-item:hover {
          background: var(--panel-2);
        }
        
        .keyword-item.selected {
          background: #007bff;
          border-color: #007bff;
          color: white;
        }
        
        .keyword-item.selected .keyword-word,
        .keyword-item.selected .keyword-freq,
        .keyword-item.selected .keyword-examples,
        .keyword-item.selected .keyword-suggested-category {
          color: white;
        }
        
        .keyword-info {
          display: flex;
          flex-direction: column;
        }
        
        .keyword-word {
          font-weight: bold;
          font-size: 16px;
          color: var(--text);
        }
        
        .keyword-freq {
          font-size: 12px;
          color: var(--muted);
        }
        
        .keyword-examples {
          font-size: 11px;
          color: var(--muted);
          font-style: italic;
        }
        
        .keyword-suggested-category {
          font-size: 12px;
          color: var(--muted);
          text-align: right;
        }
        
        .review-actions {
          display: flex;
          justify-content: space-between;
          gap: 15px;
        }
        
        .updated-section {
          text-align: center;
          padding: 60px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 8px;
        }
        
        .updated-section h3 {
          color: var(--text);
        }
        
        .updated-section p {
          color: var(--muted);
        }
        
        .success-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }
        
        .updated-actions {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-top: 30px;
        }
        
        .btn-primary {
          background: #007bff;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }
        
        .btn-primary:hover {
          background: #3399ff;
        }
        
        .btn-secondary {
          background: var(--panel-2);
          color: var(--text);
          border: 1px solid var(--border);
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }
        
        .btn-small {
          background: rgba(100, 181, 246, 0.15);
          color: #64b5f6;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          margin-left: 5px;
        }
        
        .error-message {
          background: rgba(244, 67, 54, 0.15);
          color: #ff8a80;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        
        .success-message {
          background: rgba(76, 175, 80, 0.15);
          color: #7bd88a;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        
        .current-kernels-view {
          background: var(--panel-2);
          padding: 20px;
          border-radius: 8px;
          margin-top: 20px;
        }
        
        .current-kernels-view h4 {
          color: var(--text);
        }
        
        .kernel-category {
          padding: 10px;
          border-bottom: 1px solid var(--border);
          color: var(--muted);
        }
        
        .kernel-category strong {
          color: var(--text);
        }
        
        .no-keywords {
          text-align: center;
          color: var(--muted);
          padding: 40px;
        }
      `}</style>
    </div>
  );
};

export default AIKernelBuilderScreen;
